import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ActivityIndicator } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { useNavigation } from '@react-navigation/native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { HealthDataMeasurementType, UiStateNames, UserGoalMetricKind } from '@src/types'
import { goalMetricsSelector } from '@src/selectors/app'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import { topPriorityCoachAssignmentSelector } from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'
import { ImageWithPlaceholder } from '@src/components/Image/FastImageWithPlaceholder'
import { ImageStyle } from '@src/components/LoadingFastImage'
import { TouchableSection } from '@src/components'
import { useIsDarkMode } from '@src/config/theme'
import { RECOMMENDED_WEIGHT_LOSS_RATE } from '@src/screens/NutritionistHub/components/SetWeightLossGoalScreen'
import { useUnitSystemMeasurements } from '@src/screens/Measurements/hooks'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { useShowWeightLossRecurringCallsBanner } from './hooks/useShowWeightLossRecurringCallsBanner'

export const WeightLossRecurringCallsBanner = () => {
  const navigation = useNavigation()
  const measurementUnitConfig = useUnitSystemMeasurements()
  const styles = useStyleSheet(themedStyles)
  const theme = useTheme()
  const isDarkMode = useIsDarkMode()
  const dispatch = useDispatch()
  const notificationInboxEnabled = useFeatureFlag(Feature.NotificationInbox)

  const goalMetrics = useSelector(goalMetricsSelector)
  const coachAssignment = useSelector(topPriorityCoachAssignmentSelector)

  const nutritionist = coachAssignment?.coach
  const { photo } = nutritionist?.coachProfile || {}
  const { units } = measurementUnitConfig[HealthDataMeasurementType.Weight] || {}

  const { showBanner } = useShowWeightLossRecurringCallsBanner()

  useLazyScreenPreload('ConfirmWeightLossRecurrence', { shouldPreload: showBanner })

  if (notificationInboxEnabled) {
    return null
  }

  if (!showBanner) {
    return null
  }

  const weightLossGoalMetric = goalMetrics.find(
    (metric) => metric.kind === UserGoalMetricKind.MeasurementsWeight,
  )

  const onPress = () => {
    dispatch({
      type: 'app/upsertUiState',
      payload: {
        name: UiStateNames.WeightLossRecurringBannerClicked,
        value: true,
      },
      complete: () => {
        dispatch({
          type: 'uiFlags/updateUiFlags',
          payload: { showWeightLossRecurringCallsBanner: false },
        })
      },
    })

    if (
      weightLossGoalMetric &&
      weightLossGoalMetric.highThresholdValue &&
      weightLossGoalMetric.lowThresholdValue
    ) {
      return navigation.navigate('ConfirmWeightLossRecurrence', {
        weightLossRate:
          RECOMMENDED_WEIGHT_LOSS_RATE[(units as 'pounds' | 'kilos' | undefined) || 'pounds'],
        currentWeight: weightLossGoalMetric.highThresholdValue,
      })
    }
    return navigation.navigate('SetWeightLossGoal')
  }

  const textColor = isDarkMode ? theme['theme.tooltip.textPrimary'] : theme['theme.text.primary']
  const backgroundColor = isDarkMode
    ? theme['theme.marketing.yellow']
    : theme['theme.marketing.lightYellow']
  const rightArrowColor = isDarkMode ? theme['theme.solid.black'] : undefined

  return (
    <TouchableSection
      title="Hit your weight loss goal!"
      subtitle="Schedule frequent calls with your nutritionist and start seeing results!"
      onPress={onPress}
      style={[styles.content, { backgroundColor }]}
      subtitleStyle={[styles.subtitle, { color: textColor }]}
      labelStyle={{ color: textColor }}
      accessoryLeft={
        photo && (
          <ImageWithPlaceholder
            style={styles.nutritionistPhoto as ImageStyle}
            source={{ uri: photo }}
            placeholderComponent={<ActivityIndicator animating size="small" />}
          />
        )
      }
      showRightArrow
      rightArrowColor={rightArrowColor}
    />
  )
}

const themedStyles = StyleService.create({
  subtitle: {
    maxWidth: 246,
  },
  content: {
    borderRadius: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
  },
  nutritionistPhoto: {
    width: 56,
    height: 56,
    borderRadius: 28,
    marginRight: 16,
  },
})
